import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { movimentiApi } from '@/api/magazzino'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

const euro = (cents) => new Intl.NumberFormat('it-IT', {
  style: 'currency', currency: 'EUR',
}).format((cents || 0) / 100)

const causeLabels = {
  acquisto: 'Acquisto', rientro: 'Rientro / reso', spedizione: 'Spedizione ordine',
  assemblaggio: 'Assemblaggio', disassemblaggio: 'Disassemblaggio',
  scarto: 'Scarto / rottura', uso_interno: 'Uso interno',
  rettifica: 'Rettifica inventario', altro: 'Altro / non classificato',
}

function FlowColumn({ title, data, direction }) {
  const total = data[direction]
  const causes = data.causali.filter((cause) => cause.direzione === direction)
  return (
    <div className="rounded-lg border p-4 space-y-3">
      <div>
        <p className="text-sm text-muted-foreground">{title}</p>
        <p className="text-2xl font-semibold">{euro(total.valore_cents)}</p>
        <p className="text-xs text-muted-foreground">{total.movimenti} movimenti · {euro(total.valore_stimato_cents)} stimati</p>
      </div>
      <div className="border-t pt-2 space-y-1 text-sm">
        {causes.map((cause) => (
          <div key={cause.categoria} className="flex justify-between gap-3">
            <span>{causeLabels[cause.categoria] || cause.categoria}</span>
            <span className="font-medium whitespace-nowrap">{euro(cause.valore_cents)}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

function GroupReport({ title, data }) {
  const [ordering, setOrdering] = useState('nome')
  const fields = {
    nome: item => item.nome,
    carico_quantita: item => item.carico.quantita,
    carico_valore: item => item.carico.valore_cents,
    scarico_quantita: item => item.scarico.quantita,
    scarico_valore: item => item.scarico.valore_cents,
  }
  const field = ordering.replace(/^-/, '')
  const articoli = [...data.articoli].sort((a, b) => {
    const av = fields[field](a)
    const bv = fields[field](b)
    const cmp = typeof av === 'string' ? av.localeCompare(bv, 'it', { sensitivity: 'base' }) : av - bv
    return (ordering.startsWith('-') ? -cmp : cmp) || String(a.id).localeCompare(String(b.id))
  })
  const head = (key, label, align = '') => (
    <th className={`py-2 pr-4 ${align}`} aria-sort={field === key ? (ordering.startsWith('-') ? 'descending' : 'ascending') : 'none'}>
      <button type="button" className="inline-flex items-center gap-1 hover:text-primary" onClick={() => setOrdering(ordering === key ? `-${key}` : key)}>
        {label} <span aria-hidden="true">{field === key ? (ordering.startsWith('-') ? '↓' : '↑') : '↕'}</span>
      </button>
    </th>
  )
  return (
    <div className="space-y-3">
      <h3 className="text-lg font-semibold">{title}</h3>
      <div className="grid gap-3 md:grid-cols-2">
        <FlowColumn title="Carichi fisici · valore al costo" data={data} direction="carico" />
        <FlowColumn title="Scarichi fisici · valore al costo" data={data} direction="scarico" />
      </div>
      <details className="rounded-lg border p-3">
        <summary className="cursor-pointer font-medium">Distinta per articolo ({data.articoli.length})</summary>
        <div className="mt-3 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-muted-foreground">
                {head('nome', 'Articolo')}
                {head('carico_quantita', 'Caricato', 'text-right')}
                {head('carico_valore', 'Valore caricato', 'text-right')}
                {head('scarico_quantita', 'Scaricato', 'text-right')}
                {head('scarico_valore', 'Valore scaricato', 'text-right')}
              </tr>
            </thead>
            <tbody className="readable-rows">
              {articoli.map((item) => (
                <tr key={item.id} className="border-b last:border-0">
                  <td className="py-2 pr-4">
                    <span className="font-medium">{item.nome}</span><br />
                    <span className="text-xs text-muted-foreground">{item.codice}</span>
                    <details className="mt-1">
                      <summary className="cursor-pointer text-xs text-primary">Dettaglio causali</summary>
                      <ul className="mt-1 space-y-1 text-xs text-muted-foreground">
                        {item.causali.map((cause) => (
                          <li key={`${cause.direzione}-${cause.categoria}`}>
                            {cause.direzione === 'carico' ? 'Carico' : 'Scarico'} · {causeLabels[cause.categoria] || cause.categoria}: {cause.quantita} {item.unita} · {euro(cause.valore_cents)}
                          </li>
                        ))}
                      </ul>
                    </details>
                  </td>
                  <td className="py-2 pr-4 text-right">{item.carico.quantita} {item.unita}</td>
                  <td className="py-2 pr-4 text-right">{euro(item.carico.valore_cents)}</td>
                  <td className="py-2 pr-4 text-right">{item.scarico.quantita} {item.unita}</td>
                  <td className="py-2 text-right">{euro(item.scarico.valore_cents)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {data.articoli.length === 0 && <p className="py-4 text-sm text-muted-foreground">Nessun movimento nel periodo.</p>}
        </div>
      </details>
    </div>
  )
}

export default function InventoryFlowReport() {
  const [dal, setDal] = useState('')
  const [al, setAl] = useState('')
  const validRange = !dal || !al || dal <= al
  const { data, isLoading, isError } = useQuery({
    queryKey: ['movimenti-flussi', { dal, al }],
    queryFn: () => movimentiApi.riepilogoFlussi({ ...(dal && { dal }), ...(al && { al }) }),
    enabled: validRange,
  })
  const report = data?.data
  const causeValue = (category) => report
    ? ['prodotti', 'componenti'].reduce((sum, type) => sum + report[type].causali
      .filter((cause) => cause.categoria === category)
      .reduce((subtotal, cause) => subtotal + cause.valore_cents, 0), 0)
    : 0

  return (
    <Card>
      <CardHeader>
        <CardTitle>Carichi e scarichi di magazzino</CardTitle>
        <p className="text-sm text-muted-foreground">Movimenti fisici, con valore al costo e distinta separata per prodotti e componenti.</p>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="flex flex-wrap items-end gap-3">
          <div className="space-y-1"><Label htmlFor="flussi-dal">Dal</Label><Input id="flussi-dal" type="date" value={dal} onChange={(event) => setDal(event.target.value)} /></div>
          <div className="space-y-1"><Label htmlFor="flussi-al">Al</Label><Input id="flussi-al" type="date" value={al} onChange={(event) => setAl(event.target.value)} /></div>
          <Button variant="outline" onClick={() => { setDal(''); setAl('') }} disabled={!dal && !al}>Tutto lo storico</Button>
        </div>
        {!validRange && <p role="alert" className="text-sm text-red-600">La data finale precede quella iniziale.</p>}
        {validRange && isLoading && <p className="text-sm text-muted-foreground">Calcolo dei movimenti in corso...</p>}
        {validRange && isError && <p role="alert" className="text-sm text-red-600">Impossibile caricare i movimenti. Riprova aggiornando la pagina.</p>}
        {validRange && report && <>
          <div className="grid gap-3 md:grid-cols-3">
            <div className="rounded-lg border p-4">
              <p className="text-sm text-muted-foreground">Acquisti riconosciuti · al costo</p>
              <p className="text-2xl font-semibold">{euro(causeValue('acquisto'))}</p>
              <p className="text-xs text-muted-foreground">Solo carichi con causale acquisto; lo storico precedente può essere incompleto.</p>
            </div>
            <div className="rounded-lg border border-primary/30 bg-primary/5 p-4">
              <p className="text-sm text-muted-foreground">Valore merce negli ordini confermati</p>
              <p className="text-2xl font-semibold">{euro(report.vendite_ordini.valore_merce_cents)}</p>
              <p className="text-xs text-muted-foreground">{report.vendite_ordini.ordini} ordini · al netto degli sconti, senza spedizione. Include box; può comprendere ordini non ancora spediti. Non è un dato di incasso.</p>
            </div>
            <div className="rounded-lg border p-4">
              <p className="text-sm text-muted-foreground">Scarti dichiarati · al costo</p>
              <p className="text-2xl font-semibold">{euro(causeValue('scarto'))}</p>
              <p className="text-xs text-muted-foreground">Solo scarichi classificati come scarto o rottura.</p>
            </div>
          </div>
          <GroupReport title="Prodotti" data={report.prodotti} />
          <GroupReport title="Componenti" data={report.componenti} />
          <div className="rounded-md bg-muted p-3 text-sm text-muted-foreground space-y-1">
            <p>I carichi includono anche le rettifiche; solo la voce “Acquisto” indica acquisti registrati come tali. Gli scarichi comprendono assemblaggi, scarti e spedizioni: non corrispondono al valore degli ordini.</p>
            <p>Il filtro usa la data del movimento; per gli ordini usa la data di conferma, oppure la data ordine se manca.</p>
            <p>Valori stimati: prezzi di catalogo per lo storico senza costo registrato o per movimenti automatici. Causali ricavate dallo storico libero: {report.movimenti_classificati_da_storico}. Le vendite delle box assemblate sono escluse da questa distinta per evitare doppi conteggi; consulta gli ordini per il ricavo di vendita. Movimenti box esclusi: {report.movimenti_box_esclusi}.</p>
          </div>
        </>}
      </CardContent>
    </Card>
  )
}
